/**
 * useCanCancelAcceptedProposal
 *
 * Consulta se o usuário atual pode CANCELAR uma proposta já ACEITA.
 *
 * A regra é resolvida 100% no servidor pela RPC `can_cancel_accepted_proposal`,
 * que espelha exatamente a validação de `update_proposal_status` (v1.1.0):
 *
 * - Workspace com `phase_permissions_enabled = true` → matriz por fase
 *   (`crm_phase_permissions.can_cancel_accepted_proposal`).
 * - Workspace com o toggle desligado → chave global de perfil
 *   `crm_proposals_cancel_accepted` (ação `can_delete`).
 * - Administradores (root_admin / tenant_admin) → sempre podem.
 *
 * IMPORTANTE: este hook serve apenas para mostrar/ocultar a ação na UI.
 * A segurança real permanece no backend.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-12 v1.0.0 - Criação inicial
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useCanCancelAcceptedProposal');

/**
 * @param proposalId - ID da proposta (null desabilita a consulta)
 * @param enabled - Só consulta quando realmente necessário (ex.: proposta aceita)
 */
export function useCanCancelAcceptedProposal(proposalId: string | null, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  const { data, isLoading } = useQuery({
    queryKey: ['can-cancel-accepted-proposal', tenantId, proposalId],
    enabled: enabled && !!proposalId && !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('can_cancel_accepted_proposal', {
        p_proposal_id: proposalId,
        p_tenant_hint: tenantId ?? null,
      });

      if (error) {
        // Falha na checagem → deny by default (não expõe a ação)
        logger.error('Erro ao verificar permissão de cancelar proposta aceita', {
          error: error.message,
        });
        return false;
      }

      return Boolean(data);
    },
  });

  return { canCancelAccepted: data === true, isLoading };
}
